// Logo da transportadora (PO1-2722) — endpoints do backend_hub. Usar `api` (API_URL).
//
//   POST   /meufrete/cadastro/estabelecimentos/:id/logo  → { logo, logoUrl }  (transportadora)
//   DELETE /meufrete/cadastro/estabelecimentos/:id/logo  → { removido: true, logoUrl: null }
//   POST   /transportadoras/:id/cadastro/logo            → idem, pela Everlog (admin)
//   DELETE /transportadoras/:id/cadastro/logo
//   GET    /transportadoras/:id/logo?v=<id>              → a imagem, pública, com cache
//
// `logoUrl` chega nas listagens (catálogo, carteira, detalhe, cadastro) como path
// relativo à API — `logoSrc()` monta o absoluto para o <img>. Só PNG e JPG; o
// arquivo é reduzido para caber em 640×320, sem cortar, aqui no navegador, antes de subir (o backend
// confere magic bytes e dimensões, mas não redimensiona). O interceptor de
// services/api.js remove o Content-Type quando o body é FormData.
import api from "./api";
import env from "../environment";
import { resizeImageContainToBlob } from "../utils/resizeImage";

export const LOGO_MIMES = ["image/png", "image/jpeg"];
export const LOGO_ACCEPT = LOGO_MIMES.join(",");
export const LOGO_MAX_BYTES = 3 * 1024 * 1024;
// PO1-3317: o logo cabe inteiro numa caixa 640×320 (antes era um crop central
// 256×256, que cortava as pontas de logo horizontal na faixa do catálogo).
export const LOGO_MAX_LARGURA_PX = 640;
export const LOGO_MAX_ALTURA_PX = 320;
export const LOGO_LEGENDA = "PNG ou JPG · até 3 MB · a imagem é reduzida sem cortar · logo horizontal fica melhor";

// Validação client-side antes de qualquer request (as mesmas regras do backend).
export function validarArquivoLogo(file) {
  if (!file) return { valido: false, erro: "Selecione a imagem do logo." };
  if (LOGO_MIMES.indexOf(file.type) === -1) {
    return { valido: false, erro: "Formato não suportado. Envie um PNG ou JPG (SVG não é aceito)." };
  }
  if (!file.size) return { valido: false, erro: "Arquivo vazio." };
  if (file.size > LOGO_MAX_BYTES) return { valido: false, erro: "Imagem acima de 3 MB." };
  return { valido: true, erro: "" };
}

// Reduz para caber em 640×320 (PNG, sem cortar). Devolve o Blob e o nome com que vai no multipart.
export async function prepararLogo(file) {
  const blob = await resizeImageContainToBlob(file, LOGO_MAX_LARGURA_PX, LOGO_MAX_ALTURA_PX, { mime: "image/png" });
  const base = String(file.name || "logo").replace(/\.[^.]+$/, "");
  return { blob, nome: `${base}.png` };
}

// URL absoluta para o <img>: o backend devolve o path relativo à API.
export function logoSrc(logoUrl) {
  if (!logoUrl) return null;
  if (/^https?:\/\//i.test(logoUrl)) return logoUrl;
  const base = String(env.API_URL || "").replace(/\/+$/, "");
  return `${base}${logoUrl}`;
}

function formLogo(blob, nome) {
  const form = new FormData();
  form.append("logo", blob, nome || "logo.png");
  return form;
}

export async function enviarLogoMeuFrete(idEstabelecimento, blob, nome) {
  const { data } = await api.post(`/meufrete/cadastro/estabelecimentos/${idEstabelecimento}/logo`, formLogo(blob, nome));
  return data;
}

export async function removerLogoMeuFrete(idEstabelecimento) {
  const { data } = await api.delete(`/meufrete/cadastro/estabelecimentos/${idEstabelecimento}/logo`);
  return data;
}

export async function adminEnviarLogo(idTransportadora, blob, nome) {
  const { data } = await api.post(`/transportadoras/${idTransportadora}/cadastro/logo`, formLogo(blob, nome));
  return data;
}

export async function adminRemoverLogo(idTransportadora) {
  const { data } = await api.delete(`/transportadoras/${idTransportadora}/cadastro/logo`);
  return data;
}

// Mensagem no padrão do backend ({ error, code }), com o 413 do proxy tratado.
export function mensagemErroLogo(err, fallback) {
  const status = err && err.response && err.response.status;
  const d = (err && err.response && err.response.data) || {};
  if (status === 413) return "Imagem grande demais para o servidor. Use até 3 MB.";
  if (typeof d.error === "string" && d.error) return d.error;
  if (d.message) return d.message;
  return fallback || "Não foi possível atualizar o logo.";
}
